import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { Factory, FileText, Send, Package, X } from "lucide-react";

const money = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

export default function ManufacturerDashboard() {
  const [apps, setApps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("requests");
  const [quoteFor, setQuoteFor] = useState(null);
  const [q, setQ] = useState({ materials: 800, manufacturing: 600, transport: 150, installation: 450, delay_days: 21, validity_days: 30, conditions: "Acompte de 30% à la commande." });

  const load = () => { api.get("/applications").then((r) => setApps(r.data)); api.get("/orders").then((r) => setOrders(r.data)); };
  useEffect(() => { load(); }, []);

  const sendQuote = async () => {
    try {
      await api.post("/quotes", { application_id: quoteFor.id, validity_days: +q.validity_days, delay_days: +q.delay_days, tax_rate: 20, conditions: q.conditions, line_items: { materials: +q.materials, manufacturing: +q.manufacturing, transport: +q.transport, installation: +q.installation } });
      toast.success("Devis envoyé au commerçant");
      setQuoteFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const advance = async (orderId, status) => {
    try { await api.patch(`/orders/${orderId}/status`, { status }); toast.success("Commande mise à jour"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const requests = apps.filter((a) => ["MANUFACTURER_SELECTION", "QUOTE_PENDING"].includes(a.status));
  const field = "w-full border border-input px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-[#000091]";
  const total = ((+q.materials + +q.manufacturing + +q.transport + +q.installation) * 1.2).toFixed(2);

  const nextStatus = { ORDERED: ["IN_PRODUCTION", "Lancer la fabrication"], IN_PRODUCTION: ["INSTALLATION_SCHEDULED", "Planifier la pose"], INSTALLATION_SCHEDULED: ["INSTALLED", "Déclarer installé"] };

  return (
    <div className="max-w-6xl">
      <div className="mb-6"><div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Marketplace fabricant</div><h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">Demandes & commandes</h1></div>

      <div className="flex gap-2 mb-5">
        {[["requests", "Demandes qualifiées", requests.length], ["orders", "Mes commandes", orders.length]].map(([v, l, n]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`} className={`px-4 py-2 text-sm font-semibold border transition-colors ${tab === v ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}>{l} ({n})</button>
        ))}
      </div>

      {tab === "requests" && (
        <div className="space-y-3">
          {requests.length === 0 && <div className="card-flat p-10 text-center text-slate-500"><Factory size={36} className="mx-auto text-slate-300 mb-3" />Aucune demande qualifiée pour le moment.</div>}
          {requests.map((a) => (
            <div key={a.id} className="card-flat p-5" data-testid={`request-${a.reference}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-lg">{a.commercial?.business_name || a.merchant_name}</div>
                  <div className="text-sm text-slate-500 font-mono">{a.reference} · {a.sign_type} · {a.address?.city}</div>
                  <div className="text-sm text-slate-600 mt-2">Dimensions : {a.characteristics?.width_cm}×{a.characteristics?.height_cm} cm · Matériaux : {(a.characteristics?.materials || []).join(", ")}</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={a.status} />
                  <button onClick={() => setQuoteFor(a)} data-testid={`quote-btn-${a.reference}`} className="bg-[#000091] text-white px-4 py-2 text-sm font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2"><Send size={15} /> Envoyer un devis</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && <div className="card-flat p-10 text-center text-slate-500"><Package size={36} className="mx-auto text-slate-300 mb-3" />Aucune commande.</div>}
          {orders.map((o) => (
            <div key={o.id} className="card-flat p-5" data-testid={`order-${o.reference}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div><div className="font-semibold">{o.reference}</div><div className="text-sm text-slate-500">Montant : {money(o.amount)} · {STATUS_LABELS[o.status] || o.status}</div></div>
                {nextStatus[o.status] && (
                  <button onClick={() => advance(o.id, nextStatus[o.status][0])} data-testid={`advance-${o.reference}`} className="border border-[#000091] text-[#000091] px-4 py-2 text-sm font-semibold hover:bg-[#000091] hover:text-white transition-colors">{nextStatus[o.status][1]}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {quoteFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setQuoteFor(null)}>
          <div className="bg-white border border-border w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-display font-bold text-xl">Devis — {quoteFor.reference}</h3><button onClick={() => setQuoteFor(null)}><X size={20} /></button></div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[["materials", "Matériaux (€)"], ["manufacturing", "Fabrication (€)"], ["transport", "Transport (€)"], ["installation", "Installation (€)"], ["delay_days", "Délai (jours)"], ["validity_days", "Validité (jours)"]].map(([k, l]) => (
                <div key={k}><label className="text-sm font-medium block mb-1">{l}</label><input type="number" className={field} value={q[k]} onChange={(e) => setQ({ ...q, [k]: e.target.value })} /></div>
              ))}
            </div>
            <div><label className="text-sm font-medium block mb-1">Conditions</label><textarea className={field} rows={2} value={q.conditions} onChange={(e) => setQ({ ...q, conditions: e.target.value })} /></div>
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm">Total TTC : <b className="text-xl font-display">{money(+total)}</b></div>
              <button onClick={sendQuote} data-testid="send-quote-btn" className="bg-[#000091] text-white px-5 py-2.5 font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2"><Send size={16} /> Envoyer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
